import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth } from '@saas-kit/lib';
import { toast } from '@saas-kit/ui';

import {
  createDisplay,
  deleteDisplay,
  listCounterGroups,
  listCounters,
  listDisplays,
  rotateDisplayToken,
  updateDisplay,
} from './api';
import type { CreateDisplayInput } from './apiDisplays';
import type { QmsDisplay, QmsDisplayWithToken } from './types';

export interface RevealedLink {
  name: string;
  url: string;
}

function buildPublicUrl(display: QmsDisplayWithToken): string {
  const token = encodeURIComponent(display.signed_token);
  return `${window.location.origin}/qms/display/${display.slug}?t=${token}`;
}

/** Queries, mutations, and the one-time link reveal for the Displays tab. */
export function useDisplays() {
  const { t } = useTranslation('qms');
  const tenantId = useAuth((s) => s.selectedTenant?.tenant_id);
  const queryClient = useQueryClient();

  const displaysQuery = useQuery({
    queryKey: ['qms-displays', tenantId],
    queryFn: listDisplays,
  });
  const groupsQuery = useQuery({
    queryKey: ['qms-counter-groups', tenantId],
    queryFn: listCounterGroups,
  });
  const countersQuery = useQuery({
    queryKey: ['qms-counters', tenantId],
    queryFn: listCounters,
  });

  const [revealed, setRevealed] = useState<RevealedLink | null>(null);

  const targetNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groupsQuery.data ?? []) {
      map.set(`group:${group.id}`, group.name);
    }
    for (const counter of countersQuery.data ?? []) {
      map.set(`counter:${counter.id}`, counter.name);
    }
    return map;
  }, [groupsQuery.data, countersQuery.data]);

  const fail = (e: unknown): void => {
    toast.error(parseApiError(e).message);
  };
  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['qms-displays'] });
  const reveal = (display: QmsDisplayWithToken): void =>
    setRevealed({ name: display.name, url: buildPublicUrl(display) });

  const createMutation = useMutation({
    mutationFn: createDisplay,
    onSuccess: async (created) => {
      await invalidate();
      toast.success(t('toasts.created'));
      reveal(created);
    },
    onError: fail,
  });
  const rotateMutation = useMutation({
    mutationFn: rotateDisplayToken,
    onSuccess: async (rotated) => {
      await invalidate();
      toast.success(t('displays.rotated'));
      reveal(rotated);
    },
    onError: fail,
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      updateDisplay(id, input),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.updated'));
    },
    onError: fail,
  });
  const deleteMutation = useMutation({
    mutationFn: deleteDisplay,
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.deleted'));
    },
    onError: fail,
  });
  const deleteManyMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await deleteDisplay(id);
    },
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.deleted'));
    },
    onError: fail,
  });

  const resolveTarget = (display: QmsDisplay): string => {
    const { layout } = display;
    if (layout.kind === 'all') {
      return t('displays.kind_all');
    }
    if (layout.kind === 'counter' && layout.counter_id) {
      return (
        targetNames.get(`counter:${layout.counter_id}`) ??
        t('displays.unknown_target')
      );
    }
    if (layout.kind === 'group' && layout.group_id) {
      return (
        targetNames.get(`group:${layout.group_id}`) ??
        t('displays.unknown_target')
      );
    }
    return t('displays.unknown_target');
  };

  return {
    displays: displaysQuery.data ?? [],
    isLoading: displaysQuery.isLoading,
    isError: displaysQuery.isError,
    groups: groupsQuery.data ?? [],
    counters: countersQuery.data ?? [],
    revealed,
    dismissRevealed: () => setRevealed(null),
    resolveTarget,
    create: (input: CreateDisplayInput) => createMutation.mutateAsync(input),
    creating: createMutation.isPending,
    update: (id: string, input: Record<string, unknown>) =>
      updateMutation.mutateAsync({ id, input }),
    updating: updateMutation.isPending,
    rotate: rotateMutation.mutate,
    rotating: rotateMutation.isPending,
    toggleActive: (display: QmsDisplay) =>
      updateMutation.mutate({
        id: display.id,
        input: { is_active: !display.is_active },
      }),
    remove: deleteMutation.mutate,
    removing: deleteMutation.isPending,
    removeMany: (ids: string[]) => deleteManyMutation.mutateAsync(ids),
  };
}
